import { useEffect, useState } from 'react'
import { normalizeZipInput, useZipLocation } from '../lib/useZipLocation'
import { useNavigate, useParams } from 'react-router-dom'
import api, { Place } from '../lib/api'

export default function PlaceDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const [item, setItem] = useState<Place | null>(null)
  const [name, setName] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [status, setStatus] = useState('ACTIVE')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const zipLookup = useZipLocation({ zip, onCity: setCity, onState: setState, lookupZip: api.geo.zipLookup })

  useEffect(() => {
    const run = async () => {
      setLoading(true)
      try {
        const res = await api.places.get(id)
        const p = res.data.place
        setItem(p)
        setName(p.name ?? '')
        setCity(p.city ?? '')
        setState(p.state ?? '')
        setZip(p.zip ?? '')
        setStatus(p.status ?? 'ACTIVE')
      } catch (err: any) {
        setError(err?.response?.data?.error ?? 'Failed to load place')
      } finally {
        setLoading(false)
      }
    }
    if (id) void run()
  }, [id])

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await api.places.update(id, { name, city, state, zip, status })
      setItem(res.data.place)
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to update place')
    } finally {
      setSaving(false)
    }
  }

  const remove = async () => {
    if (!confirm('Delete this place?')) return
    try {
      await api.places.remove(id)
      navigate('/places')
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Failed to delete place')
    }
  }

  if (loading) return <div className="max-w-3xl mx-auto p-6 text-sm text-gray-500">Loading place...</div>

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-4">
      <h1 className="text-2xl font-bold">Place {item?.name ?? ''}</h1>
      {error && <div className="border border-red-200 bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{error}</div>}
      <div className="bg-white border rounded-xl p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        <input className="border rounded px-3 py-2 text-sm" value={name} onChange={(e) => setName(e.target.value)} placeholder="Place name" />
        <select className="border rounded px-3 py-2 text-sm" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="ACTIVE">ACTIVE</option>
          <option value="INACTIVE">INACTIVE</option>
        </select>
        <input className="border rounded px-3 py-2 text-sm" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" />
        <input className="border rounded px-3 py-2 text-sm" value={state} onChange={(e) => setState(e.target.value)} placeholder="State" />
        <input className="border rounded px-3 py-2 text-sm" value={zip} onChange={(e) => setZip(normalizeZipInput(e.target.value))} placeholder="ZIP" maxLength={10} inputMode="numeric" />
        {(zipLookup.loading || zipLookup.message) && <p className="text-xs text-slate-500" role="status">{zipLookup.loading ? 'Looking up ZIP...' : zipLookup.message}</p>}
        <div className="md:col-span-2 flex gap-2">
          <button onClick={save} disabled={saving} className="bg-blue-700 text-white px-4 py-2 rounded text-sm">{saving ? 'Saving...' : 'Save'}</button>
          <button onClick={remove} className="border border-red-300 text-red-700 px-4 py-2 rounded text-sm">Delete</button>
        </div>
      </div>
    </div>
  )
}
